import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeft, ExternalLink, Shield, AlertTriangle, CheckCircle, XCircle, Loader2, Trash2, FileText } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

const JobLegitimacyReports = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<any | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    if (!authLoading && user) {
      fetchReports();
    }
  }, [user, authLoading]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("legitimacy_reports")
        .select("*")
        .eq("user_id", user?.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setReports(data || []);
    } catch (error) {
      console.error("Error fetching reports:", error);
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  const deleteReport = async (id: string) => {
    try {
      const { error } = await supabase
        .from("legitimacy_reports")
        .delete()
        .eq("id", id);

      if (error) throw error;
      setReports(reports.filter((r) => r.id !== id));
      toast.success("Report deleted");
    } catch (error) {
      console.error("Error deleting report:", error);
      toast.error("Failed to delete report");
    }
  };

  const getRiskBadge = (riskLevel: string | null) => {
    if (!riskLevel) return null;
    const level = riskLevel.toLowerCase();
    if (level === "low" || level === "safe") {
      return <Badge className="bg-green-500 text-white"><CheckCircle className="h-3 w-3 mr-1" />Low Risk</Badge>;
    }
    if (level === "medium" || level === "moderate") {
      return <Badge className="bg-yellow-500 text-white"><AlertTriangle className="h-3 w-3 mr-1" />Medium Risk</Badge>;
    }
    if (level === "high" || level === "dangerous") {
      return <Badge className="bg-red-500 text-white"><XCircle className="h-3 w-3 mr-1" />High Risk</Badge>;
    }
    return <Badge variant="outline">{riskLevel}</Badge>;
  };

  const getClassificationBadge = (classification: string | null) => {
    if (!classification) return null;
    const cls = classification.toLowerCase();
    if (cls.includes("legit") || cls.includes("real") || cls.includes("verified")) {
      return <Badge className="bg-green-600 text-white"><Shield className="h-3 w-3 mr-1" />Legitimate</Badge>;
    }
    if (cls.includes("suspicious") || cls.includes("caution")) {
      return <Badge className="bg-yellow-600 text-white"><AlertTriangle className="h-3 w-3 mr-1" />Suspicious</Badge>;
    }
    if (cls.includes("fraud") || cls.includes("scam") || cls.includes("fake")) {
      return <Badge className="bg-red-600 text-white"><XCircle className="h-3 w-3 mr-1" />Fraudulent</Badge>;
    }
    return <Badge variant="outline">{classification}</Badge>;
  };

  if (authLoading || loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="container mx-auto py-8 px-4 max-w-7xl">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="outline" onClick={() => navigate("/jobintel-360-reports")} className="bg-white">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Reports Hub
          </Button>
          <div className="flex items-center gap-4 flex-1">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Job Legitimacy Reports</h1>
              <p className="text-muted-foreground mt-1">View all your job verification results</p>
            </div>
          </div>
          <Button onClick={() => navigate("/job-check")} className="bg-emerald-600 hover:bg-emerald-700">
            <Shield className="h-4 w-4 mr-2" />
            New Verification
          </Button>
        </div>

        {reports.length === 0 ? (
          <Card className="border-2 border-emerald-400 bg-emerald-50 hover:shadow-lg hover:border-emerald-500 transition-all duration-300">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Shield className="h-16 w-16 text-emerald-500 mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Reports Yet</h3>
              <p className="text-muted-foreground mb-4 text-center">Start verifying job postings to see your reports here.</p>
              <Button onClick={() => navigate("/job-check")} className="bg-emerald-600 hover:bg-emerald-700">Verify a Job Posting</Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-2 border-emerald-400 bg-emerald-50 hover:shadow-lg hover:border-emerald-500 transition-all duration-300">
            <CardHeader>
              <CardTitle>Your Verification Reports ({reports.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Job Title</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Classification</TableHead>
                    <TableHead>Risk Level</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports.map((report) => (
                    <TableRow key={report.id}>
                      <TableCell className="font-medium">{report.job_title || "Unknown Position"}</TableCell>
                      <TableCell>{report.company_name || "Unknown Company"}</TableCell>
                      <TableCell>{getClassificationBadge(report.classification)}</TableCell>
                      <TableCell>{getRiskBadge(report.risk_level)}</TableCell>
                      <TableCell>{format(new Date(report.created_at), "MMM d, yyyy")}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => { setSelectedReport(report); setIsDialogOpen(true); }}>
                            <FileText className="h-4 w-4" />
                          </Button>
                          {report.job_url && (
                            <Button variant="ghost" size="sm" onClick={() => window.open(report.job_url, "_blank")}>
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => deleteReport(report.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader className="border-b border-emerald-200 pb-4">
              <DialogTitle className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                  <Shield className="h-5 w-5 text-white" />
                </div>
                Job Verification Report
              </DialogTitle>
            </DialogHeader>
            {selectedReport && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-sm text-muted-foreground">Job Title</p><p className="font-medium">{selectedReport.job_title || "N/A"}</p></div>
                  <div><p className="text-sm text-muted-foreground">Company</p><p className="font-medium">{selectedReport.company_name || "N/A"}</p></div>
                  <div><p className="text-sm text-muted-foreground">Classification</p>{getClassificationBadge(selectedReport.classification)}</div>
                  <div><p className="text-sm text-muted-foreground">Risk Level</p>{getRiskBadge(selectedReport.risk_level)}</div>
                </div>
                {selectedReport.report && (
                  <div className="prose dark:prose-invert max-w-none whitespace-pre-wrap">{selectedReport.report}</div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
};

export default JobLegitimacyReports;
